import React from "react";
import { consola } from "consola";

/**
 * Enhanced caching system for frequently accessed exercises
 * Tracks access patterns and provides intelligent caching with LRU eviction
 */
class ExerciseCache {
  constructor() {
    this.accessHistory = this.loadAccessHistory();
    this.cache = new Map();
    this.maxCacheSize = 100; // Maximum number of exercises to cache
    this.accessThreshold = 3; // Minimum access count to be considered "frequent"
    this.cacheExpiryTime = 30 * 60 * 1000; // 30 minutes in milliseconds
    
    // Load persisted cache from localStorage
    this.loadCache();
  }

  /**
   * Load access history from localStorage
   */
  loadAccessHistory() {
    try {
      const stored = localStorage.getItem("exerciseAccessHistory");
      return stored ? JSON.parse(stored) : {};
    } catch (error) {
      consola.error("Error loading access history:", error);
      return {};
    }
  }

  /**
   * Save access history to localStorage
   */
  saveAccessHistory() {
    try {
      localStorage.setItem(
        "exerciseAccessHistory",
        JSON.stringify(this.accessHistory),
      );
    } catch (error) {
      consola.error("Error saving access history:", error);
    }
  }

  /**
   * Load cache from localStorage
   */
  loadCache() {
    try {
      const stored = localStorage.getItem("exerciseCache");
      if (!stored) return;

      const cacheData = JSON.parse(stored);
      const now = Date.now();

      // Only load non-expired entries
      Object.entries(cacheData).forEach(([exerciseId, cached]) => {
        const age = now - cached.timestamp;
        if (age < this.cacheExpiryTime) {
          this.cache.set(exerciseId, {
            exercise: cached.exercise,
            timestamp: cached.timestamp,
          });
        }
      });

      if (this.cache.size > 0) {
        consola.info(`Loaded ${this.cache.size} exercises from localStorage cache`);
      }
    } catch (error) {
      consola.error("Error loading cache from localStorage:", error);
    }
  }

  /**
   * Save cache to localStorage
   */
  saveCache() {
    try {
      const cacheData = {};
      this.cache.forEach((cached, exerciseId) => {
        cacheData[exerciseId] = {
          exercise: cached.exercise,
          timestamp: cached.timestamp,
        };
      });

      localStorage.setItem("exerciseCache", JSON.stringify(cacheData));
    } catch (error) {
      consola.error("Error saving cache to localStorage:", error);
      // If localStorage is full, try to clear old entries
      if (error.name === "QuotaExceededError") {
        this.cleanupOldCache();
        // Try again after cleanup
        try {
          const cacheData = {};
          this.cache.forEach((cached, exerciseId) => {
            cacheData[exerciseId] = {
              exercise: cached.exercise,
              timestamp: cached.timestamp,
            };
          });
          localStorage.setItem("exerciseCache", JSON.stringify(cacheData));
        } catch (retryError) {
          consola.error("Error saving cache after cleanup:", retryError);
        }
      }
    }
  }

  /**
   * Cleanup old cache entries to free up space
   */
  cleanupOldCache() {
    const now = Date.now();
    const cutoffTime = now - this.cacheExpiryTime;
    let removedCount = 0;

    this.cache.forEach((cached, exerciseId) => {
      if (cached.timestamp < cutoffTime) {
        this.cache.delete(exerciseId);
        removedCount++;
      }
    });

    if (removedCount > 0) {
      consola.info(`Cleaned up ${removedCount} expired cache entries`);
    }
  }

  /**
   * Track access to an exercise
   * @param {string} exerciseId - The ID of the accessed exercise
   */
  trackAccess(exerciseId) {
    if (!exerciseId) return;

    if (!this.accessHistory[exerciseId]) {
      this.accessHistory[exerciseId] = {
        count: 0,
        lastAccessed: 0,
        firstAccessed: Date.now(),
      };
    }

    this.accessHistory[exerciseId].count++;
    this.accessHistory[exerciseId].lastAccessed = Date.now();

    // Save to localStorage periodically (every 10 accesses to avoid too many writes)
    if (this.accessHistory[exerciseId].count % 10 === 0) {
      this.saveAccessHistory();
    }
  }

  /**
   * Get frequently accessed exercises
   * @returns {Array} Array of exercise IDs sorted by access frequency
   */
  getFrequentlyAccessedExercises() {
    return Object.entries(this.accessHistory)
      .filter(([, data]) => data.count >= this.accessThreshold)
      .sort(([, a], [, b]) => {
        // Sort by weighted score: access count + recency factor
        const aScore =
          a.count + (Date.now() - a.lastAccessed < 24 * 60 * 60 * 1000 ? 5 : 0);
        const bScore =
          b.count + (Date.now() - b.lastAccessed < 24 * 60 * 60 * 1000 ? 5 : 0);
        return bScore - aScore;
      })
      .map(([id]) => id);
  }

  /**
   * Check if an exercise is cached and not expired
   * @param {string} exerciseId - The exercise ID to check
   * @returns {boolean} True if cached and valid
   */
  isCached(exerciseId) {
    const cached = this.cache.get(exerciseId);
    if (!cached) return false;

    const isExpired = Date.now() - cached.timestamp > this.cacheExpiryTime;
    if (isExpired) {
      this.cache.delete(exerciseId);
      return false;
    }

    return true;
  }

  /**
   * Get an exercise from cache
   * @param {string} exerciseId - The exercise ID
   * @returns {Object|null} The cached exercise or null if not found
   */
  get(exerciseId) {
    if (!this.isCached(exerciseId)) {
      return null;
    }

    const cached = this.cache.get(exerciseId);
    // Move to end (LRU mechanism)
    this.cache.delete(exerciseId);
    this.cache.set(exerciseId, cached);

    this.trackAccess(exerciseId);
    return cached.exercise;
  }

  /**
   * Store an exercise in cache
   * @param {string} exerciseId - The exercise ID
   * @param {Object} exercise - The exercise data
   */
  set(exerciseId, exercise) {
    if (!exerciseId || !exercise) return;

    // If cache is at capacity, remove least recently used item
    if (this.cache.size >= this.maxCacheSize) {
      const firstKey = this.cache.keys().next().value;
      this.cache.delete(firstKey);
    }

    this.cache.set(exerciseId, {
      exercise: { ...exercise },
      timestamp: Date.now(),
    });

    this.trackAccess(exerciseId);
    
    // Persist to localStorage (debounced to avoid too many writes)
    // Save immediately for important updates, but batch multiple sets
    if (!this.saveTimeout) {
      this.saveTimeout = setTimeout(() => {
        this.saveCache();
        this.saveTimeout = null;
      }, 1000); // Debounce saves by 1 second
    }
  }

  /**
   * Preload frequently accessed exercises into cache
   * @param {Function} exerciseLoader - Function to load exercise by ID
   */
  async preloadFrequentExercises(exerciseLoader) {
    const frequentExercises = this.getFrequentlyAccessedExercises().slice(
      0,
      20,
    ); // Top 20

    consola.info(
      `Preloading ${frequentExercises.length} frequently accessed exercises`,
    );

    const preloadPromises = frequentExercises.map(async (exerciseId) => {
      if (!this.isCached(exerciseId)) {
        try {
          const exercise = await exerciseLoader(exerciseId);
          if (exercise) {
            this.set(exerciseId, exercise);
          }
        } catch (error) {
          consola.error(`Error preloading exercise ${exerciseId}:`, error);
        }
      }
    });

    await Promise.allSettled(preloadPromises);
  }

  /**
   * Get cache statistics
   * @returns {Object} Cache statistics
   */
  getStats() {
    const totalAccesses = Object.values(this.accessHistory).reduce(
      (sum, data) => sum + data.count,
      0,
    );
    const frequentExercises = this.getFrequentlyAccessedExercises();

    return {
      cacheSize: this.cache.size,
      maxCacheSize: this.maxCacheSize,
      totalTrackedExercises: Object.keys(this.accessHistory).length,
      totalAccesses,
      frequentExercisesCount: frequentExercises.length,
      topFrequentExercises: frequentExercises.slice(0, 10),
      cacheHitRate: this.calculateHitRate(),
    };
  }

  /**
   * Calculate cache hit rate (simplified estimation)
   */
  calculateHitRate() {
    // This is a simplified calculation - in a real implementation,
    // you'd want to track actual hits vs misses
    const frequentExercises = this.getFrequentlyAccessedExercises();
    const cachedFrequentCount = frequentExercises.filter((id) =>
      this.isCached(id),
    ).length;
    return frequentExercises.length > 0
      ? (cachedFrequentCount / frequentExercises.length) * 100
      : 0;
  }

  /**
   * Clear old access history entries (cleanup)
   * @param {number} daysOld - Remove entries older than this many days
   */
  cleanupOldHistory(daysOld = 30) {
    const cutoffTime = Date.now() - daysOld * 24 * 60 * 60 * 1000;
    let removedCount = 0;

    Object.keys(this.accessHistory).forEach((exerciseId) => {
      if (this.accessHistory[exerciseId].lastAccessed < cutoffTime) {
        delete this.accessHistory[exerciseId];
        removedCount++;
      }
    });

    if (removedCount > 0) {
      this.saveAccessHistory();
      consola.info(`Cleaned up ${removedCount} old exercise access entries`);
    }
  }

  /**
   * Remove a specific exercise from cache
   * @param {string} exerciseId - The exercise ID to remove
   */
  remove(exerciseId) {
    if (!exerciseId) return;
    
    this.cache.delete(exerciseId);
    this.saveCache(); // Immediately save to localStorage
    consola.info(`Removed exercise ${exerciseId} from cache`);
  }

  /**
   * Update or add an exercise to cache
   * @param {string} exerciseId - The exercise ID
   * @param {Object} exercise - The exercise data
   */
  update(exerciseId, exercise) {
    this.set(exerciseId, exercise);
    // set() already saves to localStorage, but we ensure it's saved immediately
    if (this.saveTimeout) {
      clearTimeout(this.saveTimeout);
      this.saveTimeout = null;
    }
    this.saveCache();
  }

  /**
   * Clear all cache and history (for debugging or reset)
   */
  clear() {
    this.cache.clear();
    this.accessHistory = {};
    localStorage.removeItem("exerciseAccessHistory");
    localStorage.removeItem("exerciseCache");
    consola.info("Exercise cache and access history cleared");
  }
}

// Create a singleton instance
export const exerciseCache = new ExerciseCache();

/**
 * Hook for using exercise cache with React components
 * @returns {Object} Cache utilities and stats
 */
export const useExerciseCache = () => {
  return React.useMemo(
    () => ({
      cache: exerciseCache,
      trackAccess: (exerciseId) => exerciseCache.trackAccess(exerciseId),
      get: (exerciseId) => exerciseCache.get(exerciseId),
      set: (exerciseId, exercise) => exerciseCache.set(exerciseId, exercise),
      getStats: () => exerciseCache.getStats(),
      preloadFrequent: (loader) =>
        exerciseCache.preloadFrequentExercises(loader),
      cleanup: (days) => exerciseCache.cleanupOldHistory(days),
    }),
    [],
  );
};

export default exerciseCache;
